import { Button } from '@ps/sports-ui-component-library';
import type { Currency } from '@ps/web-core-enums';
import classNames from 'clsx';
import React, { useState } from 'react';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import Loader from '@swf/components/StarsButton/components/Loader/Loader';
import Translation from '@swf/components/Translation/Translation';
import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import type { AvailableCashoutQuote, MyBet } from '@swf/generated/types/api/sports';
import { Icon } from '@swf/sports/components';

import useCashOut from '../../hooks/useCashOut';
import useTrackCashOut from '../../hooks/useTrackCashOut';
import CashOutContainer from '../CashOutContainer/CashOutContainer';

import {
  TXTCLI_Cancel,
  TXTCLI_Cash_Out_N0,
  TXTCLI_Confirm,
  TXTWCL_Cash_Out
} from './CashOutAvailable.messages.json';
import styles from './CashOutAvailable.scss';

interface Props {
  bet: MyBet;
  cashOutQuote: AvailableCashoutQuote;
  currency?: Currency;
}

const CashOutAvailable: React.FC<Props> = ({ bet, cashOutQuote, currency }) => {
  const { formatMessage } = useRegulatoryIntl();
  const [selectedCashOutQuote, setSelectedCashOutQuote] = useState<AvailableCashoutQuote>();
  const { trackCashOut, trackCashOutConfirm, trackCashOutCancel, getTrackCashOutError } =
    useTrackCashOut();
  const { cashOut, submitting, error } = useCashOut();
  const cashOutQuoteAmount = selectedCashOutQuote ? selectedCashOutQuote.quote : cashOutQuote.quote;

  const clearSelectedCashOutQuote = () => setSelectedCashOutQuote(undefined);

  const handleConfirmCashOut = async () => {
    if (submitting || !selectedCashOutQuote) {
      return;
    }

    trackCashOutConfirm({ bet, cashOutQuote: selectedCashOutQuote });
    await cashOut(
      {
        cashOutToken: selectedCashOutQuote.cashOutToken,
        betId: Number(selectedCashOutQuote.betId),
        quote: selectedCashOutQuote.quote,
        betDelay: selectedCashOutQuote.betDelay
      },
      getTrackCashOutError({ bet, cashOutQuote })
    );
    clearSelectedCashOutQuote();
  };

  const confirmContent = submitting ? <Loader /> : <Translation {...TXTCLI_Confirm} />;

  return (
    <>
      <CashOutContainer
        title={
          <Translation
            {...TXTCLI_Cash_Out_N0}
            values={{
              0: (
                <span className={styles.monetaryValue}>
                  <CurrencyAmount dollarValue value={cashOutQuoteAmount} currency={currency} />
                </span>
              )
            }}
          />
        }
        className={error && styles.containerError}
      >
        {selectedCashOutQuote ? (
          <div className={styles.buttonsContainer}>
            <Button
              variant="secondary"
              disabled={submitting}
              onClick={() => {
                trackCashOutCancel({ bet, cashOutQuote: selectedCashOutQuote });
                clearSelectedCashOutQuote();
              }}
            >
              <Icon
                name="close-small"
                label={formatMessage(TXTCLI_Cancel)}
                className={classNames(styles.cancelIcon, { [styles.disabled]: submitting })}
              />
            </Button>
            <Button onClick={handleConfirmCashOut}>{confirmContent}</Button>
          </div>
        ) : (
          <Button
            onClick={() => {
              trackCashOut({ bet, cashOutQuote });
              setSelectedCashOutQuote(cashOutQuote);
            }}
          >
            <Translation {...TXTWCL_Cash_Out} />
          </Button>
        )}
      </CashOutContainer>
      {error && (
        <div role="alert" className={styles.error}>
          <Icon name="error" className={styles.errorIcon} size="x2" />
          <span>{error}</span>
        </div>
      )}
    </>
  );
};

export default CashOutAvailable;
